import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { AppLayout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { 
  Heart,
  Copy,
  Check,
  Trash2,
  Radio,
  Tv,
  Film,
  ImageIcon,
  FileText,
  Download,
  Filter,
  ChevronDown,
  ChevronRight,
  Folder,
  FolderOpen,
  TrendingUp,
  Zap
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import jsPDF from 'jspdf';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MASCOT_URL = "https://customer-assets.emergentagent.com/job_brandbrain-ai/artifacts/wegfskta_SILLON.png";

export default function LibraryPage() {
  const { t, language } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const [ideas, setIdeas] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copiedId, setCopiedId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [expandedCampaigns, setExpandedCampaigns] = useState(new Set());
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showBulkDelete, setShowBulkDelete] = useState(false);
  
  const [filters, setFilters] = useState({
    favoritesOnly: searchParams.get('favorites') === 'true',
    brandId: ''
  });

  const mediumOptions = {
    radio: { icon: Radio, label: 'Radio' },
    tv: { icon: Tv, label: 'TV' },
    reel: { icon: Film, label: 'Reel' },
    story: { icon: ImageIcon, label: 'Story' },
    post: { icon: FileText, label: 'Post' },
  };

  useEffect(() => {
    fetchData();
  }, [filters]);

  const fetchData = async () => {
    try {
      const [ideasRes, brandsRes] = await Promise.all([
        axios.get(`${API}/ideas`, { 
          params: { 
            favorites_only: filters.favoritesOnly,
            brand_id: filters.brandId || undefined
          },
          withCredentials: true 
        }),
        axios.get(`${API}/brands`, { withCredentials: true })
      ]);
      setIdeas(ideasRes.data);
      setBrands(brandsRes.data);
      
      // Auto-expand all campaigns on first load
      const campaigns = new Set(ideasRes.data.map(idea => idea.campaign_name || 'Sin campaña'));
      setExpandedCampaigns(campaigns);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Group ideas by campaign_name
  const groupedIdeas = useMemo(() => {
    const groups = {};
    ideas.forEach(idea => {
      const campaignName = idea.campaign_name || (language === 'es' ? 'Sin campaña' : 'No campaign');
      if (!groups[campaignName]) {
        groups[campaignName] = [];
      }
      groups[campaignName].push(idea);
    });
    
    // Sort campaigns alphabetically, but put "Sin campaña" at the end
    const sortedKeys = Object.keys(groups).sort((a, b) => {
      if (a === 'Sin campaña' || a === 'No campaign') return 1;
      if (b === 'Sin campaña' || b === 'No campaign') return -1;
      return a.localeCompare(b);
    });
    
    return sortedKeys.map(key => ({
      name: key,
      ideas: groups[key].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    }));
  }, [ideas, language]);

  const handleCopy = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    toast.success(t('copied'));
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleToggleFavorite = async (ideaId) => {
    try {
      const response = await axios.put(`${API}/ideas/${ideaId}/favorite`, {}, { withCredentials: true });
      setIdeas(prev => prev.map(idea => 
        idea.idea_id === ideaId ? { ...idea, is_favorite: response.data.is_favorite } : idea
      ));
    } catch (error) {
      console.error('Error toggling favorite:', error);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API}/ideas/${deleteTarget.idea_id}`, { withCredentials: true });
      toast.success(language === 'es' ? 'Idea eliminada' : 'Idea deleted');
      setIdeas(prev => prev.filter(idea => idea.idea_id !== deleteTarget.idea_id));
      setDeleteTarget(null);
    } catch (error) {
      console.error('Error deleting idea:', error);
      toast.error(language === 'es' ? 'Error al eliminar' : 'Error deleting');
    }
  };

  const toggleExpanded = (ideaId) => {
    setExpandedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(ideaId)) {
        newSet.delete(ideaId);
      } else {
        newSet.add(ideaId);
      }
      return newSet;
    });
  };

  const toggleCampaignExpanded = (campaignName) => {
    setExpandedCampaigns(prev => {
      const newSet = new Set(prev);
      if (newSet.has(campaignName)) {
        newSet.delete(campaignName);
      } else {
        newSet.add(campaignName);
      }
      return newSet;
    });
  };

  const toggleSelected = (ideaId) => {
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(ideaId)) {
        newSet.delete(ideaId);
      } else {
        newSet.add(ideaId);
      }
      return newSet;
    });
  };

  const selectAllInCampaign = (campaignIdeas) => {
    const campaignIds = campaignIdeas.map(i => i.idea_id);
    const allSelected = campaignIds.every(id => selectedIds.has(id));
    
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      if (allSelected) {
        campaignIds.forEach(id => newSet.delete(id));
      } else {
        campaignIds.forEach(id => newSet.add(id));
      }
      return newSet;
    });
  };

  const selectAll = () => {
    if (selectedIds.size === ideas.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(ideas.map(i => i.idea_id)));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    try {
      await Promise.all(
        Array.from(selectedIds).map(id => 
          axios.delete(`${API}/ideas/${id}`, { withCredentials: true })
        )
      );
      toast.success(language === 'es' ? `${selectedIds.size} ideas eliminadas` : `${selectedIds.size} ideas deleted`);
      setIdeas(prev => prev.filter(idea => !selectedIds.has(idea.idea_id)));
      setSelectedIds(new Set());
      setShowBulkDelete(false);
    } catch (error) {
      console.error('Error bulk deleting:', error);
      toast.error(language === 'es' ? 'Error al eliminar' : 'Error deleting');
    }
  };

  const handleExportPDF = (idea) => {
    const doc = new jsPDF();
    const brand = brands.find(b => b.brand_id === idea.brand_id);
    
    doc.setFontSize(24);
    doc.setTextColor(132, 204, 22);
    doc.text('BRAIN-GL', 20, 25);
    
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text('Creative Brief', 20, 32);
    
    doc.setFontSize(16);
    doc.setTextColor(227, 24, 55);
    doc.text(brand?.name || 'Brand', 20, 50);
    
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text(`Medium: ${mediumOptions[idea.medium]?.label || idea.medium}`, 20, 58);
    doc.text(`Topic: ${idea.topic}`, 20, 65);
    if (idea.campaign_name) {
      doc.text(`Campaign: ${idea.campaign_name}`, 20, 72);
    }
    
    let yPos = idea.campaign_name ? 92 : 85;
    
    doc.setFontSize(12);
    doc.setTextColor(132, 204, 22);
    doc.text('HOOK / PROBLEM', 20, yPos);
    doc.setTextColor(60, 60, 60);
    doc.setFontSize(10);
    const hookLines = doc.splitTextToSize(idea.hook, 170);
    doc.text(hookLines, 20, yPos + 8);
    yPos += 8 + (hookLines.length * 5) + 10;
    
    doc.setFontSize(12);
    doc.setTextColor(100, 100, 100);
    doc.text('SOLUTION', 20, yPos);
    doc.setTextColor(60, 60, 60);
    doc.setFontSize(10);
    const solutionLines = doc.splitTextToSize(idea.solution, 170);
    doc.text(solutionLines, 20, yPos + 8);
    yPos += 8 + (solutionLines.length * 5) + 10;
    
    doc.setFontSize(12);
    doc.setTextColor(100, 100, 100);
    doc.text('BENEFIT', 20, yPos);
    doc.setTextColor(60, 60, 60);
    doc.setFontSize(10);
    const benefitLines = doc.splitTextToSize(idea.benefit, 170);
    doc.text(benefitLines, 20, yPos + 8);
    yPos += 8 + (benefitLines.length * 5) + 10;
    
    doc.setFontSize(12);
    doc.setTextColor(227, 24, 55);
    doc.text('CLOSING', 20, yPos);
    doc.setTextColor(60, 60, 60);
    doc.setFontSize(10);
    const closingLines = doc.splitTextToSize(idea.closing, 170);
    doc.text(closingLines, 20, yPos + 8);
    yPos += 8 + (closingLines.length * 5) + 15;
    
    if (yPos < 250) {
      doc.setFontSize(12);
      doc.setTextColor(132, 204, 22);
      doc.text('FULL COPY', 20, yPos);
      doc.setTextColor(60, 60, 60);
      doc.setFontSize(10);
      const copyLines = doc.splitTextToSize(idea.full_copy, 170);
      doc.text(copyLines, 20, yPos + 8);
    }
    
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text(`Generated with Brain-GL - ${new Date().toLocaleDateString()}`, 20, 285);
    
    doc.save(`${brand?.name || 'brand'}_${idea.campaign_name || 'idea'}_${idea.medium}.pdf`);
    toast.success(language === 'es' ? 'PDF exportado' : 'PDF exported');
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-lime-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="space-y-6 animate-fade-in" data-testid="library-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold uppercase tracking-tight" style={{ fontFamily: 'Oswald, sans-serif' }}>
              {t('myLibrary')}
            </h1>
            <p className="text-zinc-400 mt-1">
              {language === 'es' ? 'Tus ideas organizadas por campaña' : 'Your ideas organized by campaign'}
            </p>
          </div>

          {/* Filters */}
          <div className="flex items-center gap-4">
            {selectedIds.size > 0 && (
              <Button
                variant="destructive"
                size="sm"
                onClick={() => setShowBulkDelete(true)}
                className="bg-red-600 hover:bg-red-700 text-white gap-2"
                data-testid="bulk-delete-btn"
              >
                <Trash2 className="w-4 h-4" />
                {language === 'es' ? `Eliminar (${selectedIds.size})` : `Delete (${selectedIds.size})`}
              </Button>
            )}

            <Tabs 
              value={filters.favoritesOnly ? 'favorites' : 'all'} 
              onValueChange={(value) => {
                setFilters({...filters, favoritesOnly: value === 'favorites'});
                setSearchParams(value === 'favorites' ? { favorites: 'true' } : {});
              }}
            >
              <TabsList className="bg-zinc-800 border border-zinc-700">
                <TabsTrigger 
                  value="all" 
                  className="data-[state=active]:bg-lime-500 data-[state=active]:text-black"
                  data-testid="tab-all"
                >
                  {t('allIdeas')}
                </TabsTrigger>
                <TabsTrigger 
                  value="favorites"
                  className="data-[state=active]:bg-red-500 data-[state=active]:text-white"
                  data-testid="tab-favorites"
                >
                  <Heart className="w-4 h-4 mr-1" />
                  {t('favoritesOnly')}
                </TabsTrigger>
              </TabsList>
            </Tabs>

            <Select 
              value={filters.brandId || 'all'} 
              onValueChange={(value) => setFilters({...filters, brandId: value === 'all' ? '' : value})}
            >
              <SelectTrigger className="w-[180px] bg-zinc-800 border-zinc-700 text-white" data-testid="filter-brand">
                <Filter className="w-4 h-4 mr-2 text-zinc-400" />
                <SelectValue placeholder={t('filterByBrand')} />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800">
                <SelectItem value="all" className="text-zinc-300">
                  {language === 'es' ? 'Todas las marcas' : 'All brands'}
                </SelectItem>
                {brands.map((brand) => (
                  <SelectItem 
                    key={brand.brand_id} 
                    value={brand.brand_id}
                    className="text-zinc-300"
                  >
                    {brand.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Campaign Stats */}
        <div className="flex items-center gap-4 text-sm text-zinc-400">
          <span>{groupedIdeas.length} {language === 'es' ? 'campañas' : 'campaigns'}</span>
          <span>•</span>
          <span>{ideas.length} {language === 'es' ? 'ideas totales' : 'total ideas'}</span>
        </div>

        {/* Ideas List Grouped by Campaign */}
        {ideas.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20">
            <img src={MASCOT_URL} alt="Empty" className="w-40 h-40 object-contain opacity-30 mb-4" />
            <p className="text-zinc-500 text-center">{t('noIdeas')}</p>
          </div>
        ) : (
          <div className="space-y-4">
            {groupedIdeas.map((campaign) => {
              const isCampaignExpanded = expandedCampaigns.has(campaign.name);
              const campaignSelectedCount = campaign.ideas.filter(i => selectedIds.has(i.idea_id)).length;
              const allCampaignSelected = campaignSelectedCount === campaign.ideas.length;
              
              return (
                <div key={campaign.name} className="rounded-lg border border-zinc-800 overflow-hidden">
                  {/* Campaign Header / Folder */}
                  <div 
                    className="flex items-center justify-between px-4 py-3 bg-zinc-900/80 cursor-pointer hover:bg-zinc-800/50 transition-colors"
                    onClick={() => toggleCampaignExpanded(campaign.name)}
                  >
                    <div className="flex items-center gap-3">
                      <div onClick={(e) => { e.stopPropagation(); selectAllInCampaign(campaign.ideas); }}>
                        <Checkbox 
                          checked={allCampaignSelected}
                          onCheckedChange={() => selectAllInCampaign(campaign.ideas)}
                        />
                      </div>
                      {isCampaignExpanded ? (
                        <FolderOpen className="w-5 h-5 text-lime-500" />
                      ) : (
                        <Folder className="w-5 h-5 text-lime-500" />
                      )}
                      <span className="font-semibold text-white">{campaign.name}</span>
                      <span className="px-2 py-0.5 bg-zinc-700 text-zinc-300 text-xs rounded-full">
                        {campaign.ideas.length} {language === 'es' ? 'ideas' : 'ideas'}
                      </span>
                      {campaignSelectedCount > 0 && campaignSelectedCount < campaign.ideas.length && (
                        <span className="px-2 py-0.5 bg-lime-500/20 text-lime-400 text-xs rounded-full">
                          {campaignSelectedCount} {language === 'es' ? 'seleccionadas' : 'selected'}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {isCampaignExpanded ? (
                        <ChevronDown className="w-5 h-5 text-zinc-400" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-zinc-400" />
                      )}
                    </div>
                  </div>

                  {/* Campaign Ideas */}
                  {isCampaignExpanded && (
                    <div className="divide-y divide-zinc-800/50">
                      {campaign.ideas.map((idea, index) => {
                        const brand = brands.find(b => b.brand_id === idea.brand_id);
                        const MediumIcon = mediumOptions[idea.medium]?.icon || FileText;
                        const isExpanded = expandedIds.has(idea.idea_id);
                        
                        return (
                          <Collapsible 
                            key={idea.idea_id}
                            open={isExpanded}
                            onOpenChange={() => toggleExpanded(idea.idea_id)}
                          >
                            <div className="bg-zinc-900/30">
                              {/* Idea Header */}
                              <CollapsibleTrigger asChild>
                                <div className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-zinc-800/30 transition-colors pl-10">
                                  <div className="flex items-center gap-3 flex-1">
                                    <div onClick={(e) => { e.stopPropagation(); toggleSelected(idea.idea_id); }}>
                                      <Checkbox 
                                        checked={selectedIds.has(idea.idea_id)}
                                        onCheckedChange={() => toggleSelected(idea.idea_id)}
                                      />
                                    </div>
                                    <div className="text-zinc-500">
                                      {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                                    </div>
                                    <span className="text-lime-500 font-mono text-sm">#{index + 1}</span>
                                    {/* Shareability Score Badge */}
                                    {idea.shareability_score && (
                                      <div 
                                        className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                                          idea.shareability_score.level === 'viral' ? 'bg-purple-500/20 text-purple-400' :
                                          idea.shareability_score.level === 'high' ? 'bg-green-500/20 text-green-400' :
                                          idea.shareability_score.level === 'medium' ? 'bg-yellow-500/20 text-yellow-400' :
                                          'bg-zinc-500/20 text-zinc-400'
                                        }`}
                                      >
                                        {idea.shareability_score.level === 'viral' ? <Zap className="w-3 h-3" /> : <TrendingUp className="w-3 h-3" />}
                                        {idea.shareability_score.score}
                                      </div>
                                    )}
                                    <span className="text-white font-medium truncate max-w-md">{idea.hook}</span>
                                    <span className="px-2 py-0.5 bg-red-500/10 text-red-500 text-xs rounded-full border border-red-500/20">
                                      {brand?.name || 'Brand'}
                                    </span>
                                    <span className="flex items-center gap-1 px-2 py-0.5 bg-zinc-700 text-zinc-300 text-xs rounded-full">
                                      <MediumIcon className="w-3 h-3" />
                                      {mediumOptions[idea.medium]?.label}
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      onClick={() => handleToggleFavorite(idea.idea_id)}
                                      className={`h-8 w-8 ${idea.is_favorite ? 'text-red-500' : 'text-zinc-400 hover:text-red-500'}`}
                                    >
                                      <Heart className={`w-4 h-4 ${idea.is_favorite ? 'fill-current' : ''}`} />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      onClick={() => handleExportPDF(idea)}
                                      className="h-8 w-8 text-zinc-400 hover:text-lime-500"
                                    >
                                      <Download className="w-4 h-4" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      onClick={() => setDeleteTarget(idea)}
                                      className="h-8 w-8 text-zinc-400 hover:text-red-500"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </div>
                                </div>
                              </CollapsibleTrigger>

                              {/* Expanded Content */}
                              <CollapsibleContent>
                                <CardContent className="px-4 pb-4 pt-0 border-t border-zinc-800 ml-10">
                                  <div className="grid lg:grid-cols-2 gap-6 pt-4">
                                    {/* Left Column - Copy Structure */}
                                    <div className="space-y-4">
                                      <h3 className="text-sm font-semibold text-white uppercase tracking-wider">
                                        {language === 'es' ? 'Copy para Redes' : 'Copy for Social'}
                                      </h3>
                                      
                                      <div className="space-y-3">
                                        <div className="p-3 bg-lime-500/5 border border-lime-500/20 rounded-lg">
                                          <p className="text-xs font-medium text-lime-500 uppercase mb-1">HOOK</p>
                                          <p className="text-zinc-200">{idea.hook}</p>
                                        </div>
                                        
                                        <div className="p-3 bg-zinc-800/50 rounded-lg">
                                          <p className="text-xs font-medium text-zinc-500 uppercase mb-1">{language === 'es' ? 'SOLUCIÓN' : 'SOLUTION'}</p>
                                          <p className="text-zinc-300">{idea.solution}</p>
                                        </div>
                                        
                                        <div className="p-3 bg-zinc-800/50 rounded-lg">
                                          <p className="text-xs font-medium text-zinc-500 uppercase mb-1">{language === 'es' ? 'BENEFICIO' : 'BENEFIT'}</p>
                                          <p className="text-zinc-300">{idea.benefit}</p>
                                        </div>
                                        
                                        <div className="p-3 bg-red-500/5 border border-red-500/20 rounded-lg">
                                          <p className="text-xs font-medium text-red-500 uppercase mb-1">{language === 'es' ? 'CIERRE' : 'CLOSING'}</p>
                                          <p className="text-zinc-200 font-medium">{idea.closing}</p>
                                        </div>
                                      </div>

                                      {/* Full Copy */}
                                      <div className="p-4 bg-zinc-800/30 rounded-lg border border-zinc-700">
                                        <div className="flex items-center justify-between mb-2">
                                          <p className="text-xs font-medium text-zinc-400 uppercase">
                                            {language === 'es' ? 'Copy Integrado' : 'Full Copy'}
                                          </p>
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => handleCopy(idea.full_copy, `copy-${idea.idea_id}`)}
                                            className="h-7 text-zinc-400 hover:text-lime-500"
                                          >
                                            {copiedId === `copy-${idea.idea_id}` ? (
                                              <><Check className="w-3 h-3 mr-1" /> {t('copied')}</>
                                            ) : (
                                              <><Copy className="w-3 h-3 mr-1" /> {t('copyToClipboard')}</>
                                            )}
                                          </Button>
                                        </div>
                                        <p className="text-zinc-300 whitespace-pre-wrap text-sm">{idea.full_copy}</p>
                                      </div>
                                    </div>

                                    {/* Right Column - Image Prompt */}
                                    <div className="space-y-4">
                                      <h3 className="text-sm font-semibold text-white uppercase tracking-wider">
                                        {language === 'es' ? 'Prompt de Imagen' : 'Image Prompt'}
                                      </h3>
                                      
                                      <div className="p-4 bg-black rounded-lg border border-zinc-800">
                                        <div className="flex items-center justify-between mb-2">
                                          <p className="text-xs font-medium text-lime-400">{t('imagePrompt')}</p>
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => handleCopy(JSON.stringify(idea.image_prompt, null, 2), `prompt-${idea.idea_id}`)}
                                            className="h-7 text-zinc-400 hover:text-lime-500"
                                          >
                                            {copiedId === `prompt-${idea.idea_id}` ? (
                                              <><Check className="w-3 h-3 mr-1" /> {t('copied')}</>
                                            ) : (
                                              <><Copy className="w-3 h-3 mr-1" /> {t('copyToClipboard')}</>
                                            )}
                                          </Button>
                                        </div>
                                        <pre className="text-lime-400 text-xs font-mono overflow-x-auto whitespace-pre-wrap">
                                          {JSON.stringify(idea.image_prompt, null, 2)}
                                        </pre>
                                      </div>

                                      {/* Shareability Score Details */}
                                      {idea.shareability_score && (
                                        <div className={`p-4 rounded-lg border ${
                                          idea.shareability_score.level === 'viral' ? 'bg-purple-500/10 border-purple-500/30' :
                                          idea.shareability_score.level === 'high' ? 'bg-green-500/10 border-green-500/30' :
                                          idea.shareability_score.level === 'medium' ? 'bg-yellow-500/10 border-yellow-500/30' :
                                          'bg-zinc-500/10 border-zinc-500/30'
                                        }`}>
                                          <div className="flex items-center justify-between mb-2">
                                            <span className="text-xs font-medium text-zinc-400 uppercase">Shareability Score</span>
                                            <span className={`text-2xl font-bold ${
                                              idea.shareability_score.level === 'viral' ? 'text-purple-400' :
                                              idea.shareability_score.level === 'high' ? 'text-green-400' :
                                              idea.shareability_score.level === 'medium' ? 'text-yellow-400' :
                                              'text-zinc-400'
                                            }`}>
                                              {idea.shareability_score.score}/100
                                            </span>
                                          </div>
                                          <p className={`text-sm mb-2 ${
                                            idea.shareability_score.level === 'viral' ? 'text-purple-300' :
                                            idea.shareability_score.level === 'high' ? 'text-green-300' :
                                            idea.shareability_score.level === 'medium' ? 'text-yellow-300' :
                                            'text-zinc-300'
                                          }`}>
                                            {language === 'es' ? idea.shareability_score.label_es : idea.shareability_score.label_en}
                                          </p>
                                          {idea.shareability_score.factors && (
                                            <div className="grid grid-cols-2 gap-2 text-xs">
                                              {Object.entries(idea.shareability_score.factors).map(([key, value]) => (
                                                <div key={key} className="flex justify-between">
                                                  <span className="text-zinc-500 capitalize">{key.replace('_', ' ')}</span>
                                                  <span className="text-zinc-300">{value}</span>
                                                </div>
                                              ))}
                                            </div>
                                          )}
                                        </div>
                                      )}

                                      {/* Meta info */}
                                      <div className="p-3 bg-zinc-800/30 rounded-lg">
                                        <p className="text-xs text-zinc-500 mb-2 uppercase">{language === 'es' ? 'Información' : 'Information'}</p>
                                        <div className="grid grid-cols-2 gap-2 text-sm">
                                          <div>
                                            <p className="text-zinc-500">Tema</p>
                                            <p className="text-zinc-300">{idea.topic}</p>
                                          </div>
                                          <div>
                                            <p className="text-zinc-500">Modelo</p>
                                            <p className="text-zinc-300">{idea.model_used}</p>
                                          </div>
                                          <div>
                                            <p className="text-zinc-500">Fecha</p>
                                            <p className="text-zinc-300">{new Date(idea.created_at).toLocaleDateString()}</p>
                                          </div>
                                          <div>
                                            <p className="text-zinc-500">Medio</p>
                                            <p className="text-zinc-300">{mediumOptions[idea.medium]?.label}</p>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </CardContent>
                              </CollapsibleContent>
                            </div>
                          </Collapsible>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Delete Single Confirmation */}
        <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
          <AlertDialogContent className="bg-zinc-900 border-zinc-800">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-white">
                {language === 'es' ? '¿Eliminar idea?' : 'Delete idea?'}
              </AlertDialogTitle>
              <AlertDialogDescription className="text-zinc-400">
                {language === 'es' 
                  ? 'Esta acción no se puede deshacer.'
                  : 'This action cannot be undone.'}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="bg-zinc-800 border-zinc-700 text-zinc-300 hover:bg-zinc-700">
                {t('cancel')}
              </AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleDelete}
                className="bg-red-600 text-white hover:bg-red-700"
              >
                {t('delete')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Bulk Delete Confirmation */}
        <AlertDialog open={showBulkDelete} onOpenChange={setShowBulkDelete}>
          <AlertDialogContent className="bg-zinc-900 border-zinc-800">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-white">
                {language === 'es' ? `¿Eliminar ${selectedIds.size} ideas?` : `Delete ${selectedIds.size} ideas?`}
              </AlertDialogTitle>
              <AlertDialogDescription className="text-zinc-400">
                {language === 'es' 
                  ? 'Esta acción eliminará todas las ideas seleccionadas y no se puede deshacer.'
                  : 'This action will delete all selected ideas and cannot be undone.'}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="bg-zinc-800 border-zinc-700 text-zinc-300 hover:bg-zinc-700">
                {t('cancel')}
              </AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleBulkDelete}
                className="bg-red-600 text-white hover:bg-red-700"
                data-testid="confirm-bulk-delete"
              >
                {language === 'es' ? `Eliminar ${selectedIds.size}` : `Delete ${selectedIds.size}`}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AppLayout>
  );
}
